<template>
  <div>
    <AlertDismiss ref="alertDismiss"></AlertDismiss>

    <p class="text-muted mb-2">
      Hemos enviado un código de verificación a:
      <b>
        {{ email }}
      </b>
    </p>

    <b-form @submit="onSubmit">
      <b-form-group
        class="mb-3"
        id="input-group-otp"
        label="Código de Verificación"
      >
        <div class="d-flex align-items-center gap-2">
          <b-form-input
            id="input-otp"
            v-model="form.otp"
            type="text"
            placeholder="000000"
            maxlength="6"
            class="text-center"
            style="font-size: 1.2em; letter-spacing: 0.3em; width: 150px"
            required
            autocomplete="one-time-code"
          >
          </b-form-input>

          <span class="text-muted">
            ¿No recibiste el código?
            <a 
              href="#" 
              @click.prevent="resendOtp" 
              :class="{ 'disabled text-muted': !canResendOtp }"
              :style="{ pointerEvents: !canResendOtp ? 'none' : 'auto', textDecoration: !canResendOtp ? 'none' : 'underline' }"
            >
              <span v-if="resendingOtp">Reenviando...</span>
              <span v-else-if="remainingTime > 0">Reenviar en {{ formattedRemainingTime }}</span>
              <span v-else>Reenviar</span>
            </a>
          </span>
        </div>
      </b-form-group>

      <div>
        <b-button
          class="me-2"
          type="submit"
          variant="primary"
          :disabled="process || form.otp.length !== 6"
        >
          <b-spinner v-if="process" small />
          Verificar
        </b-button>

        <b-button
          variant="outline-secondary"
          @click="goBack"
          :disabled="process"
        >
          Volver
        </b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import AlertDismiss from "@/components/shared/Alert-dismiss.vue";
import config from "@/plugins/config";

export default {
  name: "OtpForm",

  props: {
    email: {
      type: String,
      required: true,
    },
    password: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      form: {
        otp: "",
      },
      process: false,
      resendingOtp: false,
      retryTimeout: null,
      remainingTime: config.otp.retryTimeout, // Start locked out
    };
  },

  components: {
    AlertDismiss,
  },

  computed: {
    formattedRemainingTime() {
      const minutes = Math.floor(this.remainingTime / 60);
      const seconds = this.remainingTime % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },

    canResendOtp() {
      return !this.resendingOtp && !this.process && this.remainingTime <= 0;
    },
  },


  mounted() {
    // Auto-focus on OTP input
    this.$nextTick(() => {
      document.getElementById("input-otp")?.focus();
    });
    
    // Start countdown immediately
    this.startCountdown();
  },

  beforeDestroy() {
    // Clear any active intervals when component is destroyed
    if (this.retryTimeout) {
      clearInterval(this.retryTimeout);
    }
  },

  methods: {
    ...mapActions(["checkOtp", "login"]),

    async onSubmit(event) {
      event.preventDefault();
      this.processing();

      try {
        const result = await this.checkOtp({
          mail: this.email,
          otp: this.form.otp,
        });

        if (result.isError) {
          // Check if the error is about expired code
          if (result.message === "Error : El código ha expirado.") {
            this.$refs.alertDismiss.showAlert(
              "El código ha expirado. Enviando un nuevo código..."
            );
            await this.resendOtpInternal();
          } else {
            this.$refs.alertDismiss.showAlert(result.message);
          }
          this.processing();
          return;
        }

        // Emit success with the JWT token
        this.$emit("otpVerified", result.data);
      } catch (error) {
        this.$refs.alertDismiss.showAlert(
          "Error al verificar el código. Inténtalo nuevamente."
        );
        this.processing();
      }
    },

    goBack() {
      this.$emit("goBack");
    },

    async resendOtp() {
      if (!this.canResendOtp) return;
      
      this.resendingOtp = true;
      try {
        const result = await this.login({
          mail: this.email,
          password: this.password,
        });

        if (result.isError) {
          this.$refs.alertDismiss.showAlert(result.message);
          return;
        }

        // Clear the OTP input after resending
        this.form.otp = "";

        // Start the retry timeout
        this.startRetryTimeout();

        this.$refs.alertDismiss.showAlert(
          "Código reenviado a tu correo electrónico.",
          "success"
        );
      } catch (error) {
        this.$refs.alertDismiss.showAlert(
          "Error al reenviar el código. Inténtalo nuevamente."
        );
      } finally {
        this.resendingOtp = false;
      }
    },

    processing() {
      this.process = !this.process;
    },

    startRetryTimeout() {
      this.remainingTime = config.otp.retryTimeout;
      this.startCountdown();
    },

    startCountdown() {
      if (this.retryTimeout) {
        clearInterval(this.retryTimeout);
      }
      
      this.retryTimeout = setInterval(() => {
        this.remainingTime--;
        
        if (this.remainingTime <= 0) {
          clearInterval(this.retryTimeout);
          this.retryTimeout = null;
        }
      }, 1000);
    },

    async resendOtpInternal() {
      // Internal method for automatic resend (e.g., when code expires)
      // This bypasses the timeout check
      if (this.resendingOtp || this.process) return;
      
      this.resendingOtp = true;
      try {
        const result = await this.login({
          mail: this.email,
          password: this.password,
        });

        if (result.isError) {
          this.$refs.alertDismiss.showAlert(result.message);
          return;
        }

        // Clear the OTP input after resending
        this.form.otp = "";

        // Start the retry timeout
        this.startRetryTimeout();

        this.$refs.alertDismiss.showAlert(
          "Código reenviado a tu correo electrónico.",
          "success"
        );
      } catch (error) {
        this.$refs.alertDismiss.showAlert(
          "Error al reenviar el código. Inténtalo nuevamente."
        );
      } finally {
        this.resendingOtp = false;
      }
    },
  },
};
</script>

<style scoped>
.text-center input {
  text-align: center;
}
</style>
